import type { TFunction } from 'i18next';
import type { BonusMinDepV2 } from '@/api/cms/bonuses';
import {
  type BonusShopApi,
  type BonusShopFs,
  BonusShopProductType,
} from '@/api/cms/bonusShop';
import {
  CurrencyCode,
  decimalPlacesMapCrypto,
  decimalPlacesMapMicroCrypto,
  getCurrencyName,
} from '@/helpers/currency';
import { getTimeLeft } from '@/helpers/timer';
import type {
  BonusShopCurrency,
  BonusShopDetail,
  BonusShopProduct,
} from './types';

const getAmount = (
  amounts: BonusMinDepV2[],
  currency: string,
  withEuroFallback: boolean
) =>
  amounts?.find((item) => getCurrencyName(item.currency) === currency) ||
  (withEuroFallback
    ? amounts?.find((item) => item.currency === CurrencyCode.EUR)
    : undefined);

const getSpinValueCurrency = ({ currency, isCrypto }: BonusShopCurrency) =>
  isCrypto ? CurrencyCode.EUR : currency;

const byOrder = (a: BonusShopProduct, b: BonusShopProduct) => {
  if (a.order == null) return b.order == null ? 0 : 1;
  if (b.order == null) return -1;

  return a.order - b.order;
};

const toDetails = (details: (BonusShopDetail | null)[]): BonusShopDetail[] =>
  details.filter((detail): detail is BonusShopDetail => !!detail?.value);

const isExpired = (durationTo: string | null) =>
  !!durationTo && getTimeLeft(durationTo) <= 0;

const FIAT_DECIMALS = 2;

const formatMoney = (amount: number, currency: string) => {
  const cryptoDecimals =
    decimalPlacesMapMicroCrypto[currency] ?? decimalPlacesMapCrypto[currency];
  const isWhole = Number.isInteger(amount);

  return amount.toLocaleString('en-US', {
    minimumFractionDigits:
      isWhole || cryptoDecimals != null ? 0 : FIAT_DECIMALS,
    maximumFractionDigits: isWhole ? 0 : (cryptoDecimals ?? FIAT_DECIMALS),
  });
};

const formatAmount = (
  amounts: BonusMinDepV2[],
  currency: string,
  withEuroFallback: boolean
): string | null => {
  const item = getAmount(amounts, currency, withEuroFallback);
  if (!item) return null;
  return `${formatMoney(item.amount, item.currency)} ${getCurrencyName(item.currency)}`;
};

const normalizeFreeSpins = (
  items: BonusShopFs[],
  walletCurrency: BonusShopCurrency,
  t: TFunction
): BonusShopProduct[] =>
  items.flatMap((item) => {
    const versions = item.fsItems.map((fsItem) => {
      const spinValue = formatAmount(
        fsItem.spinValue,
        getSpinValueCurrency(walletCurrency),
        walletCurrency.isGuest
      );

      return {
        id: fsItem.id,
        group: fsItem.group,
        fsCount: fsItem.fsCount,
        label: t('fsCount', { count: fsItem.fsCount }),
        cpPrice: fsItem.cpPrice,
        productTerms: fsItem.productTerms,
        subtitle: spinValue ? t('perSpin', { value: spinValue }) : null,
        details: toDetails([
          { label: t('details.game'), value: fsItem.game || '' },
          { label: t('details.spinValue'), value: spinValue || '' },
          { label: t('details.wager'), value: fsItem.wager || '' },
          { label: t('details.maxWin'), value: fsItem.maxWin || '' },
        ]),
      };
    });

    const [firstVersion] = versions;
    if (!firstVersion) return [];

    return [
      {
        id: `${BonusShopProductType.FREE_SPINS}-${item.id}`,
        type: BonusShopProductType.FREE_SPINS,
        order: item.order,
        group: firstVersion.group,
        img: item.img,
        label: item.label,
        title: item.title,
        subtitle: firstVersion.subtitle,
        durationTo: item.durationTo,
        cpPrice: firstVersion.cpPrice,
        productTerms: firstVersion.productTerms,
        details: firstVersion.details,
        amount: null,
        amountValue: null,
        versions,
      },
    ];
  });

export const normalizeProducts = (
  shop: BonusShopApi | null,
  freeSpins: BonusShopFs[],
  walletCurrency: BonusShopCurrency,
  t: TFunction
): BonusShopProduct[] => {
  const { currency, isGuest } = walletCurrency;

  const cash = (shop?.cash || []).flatMap((item) => {
    const amount = formatAmount(item.amount, currency, isGuest);
    if (!amount) return [];

    const amountValue =
      getAmount(item.amount, currency, isGuest)?.amount ?? null;

    return [
      {
        id: `${BonusShopProductType.CASH}-${item.id}`,
        type: BonusShopProductType.CASH,
        order: item.order,
        group: item.group,
        img: item.img,
        label: item.label,
        title: `${amount} ${t('cash')}`,
        typeName: t('cash'),
        amountValue,
        subtitle: t('zeroWager'),
        amount,
        durationTo: item.durationTo,
        cpPrice: item.cpPrice,
        productTerms: item.productTerms,
        details: [],
        versions: [],
      },
    ];
  });

  const cashBonuses = (shop?.cashBonuses || []).flatMap((item) => {
    const amount = formatAmount(item.amount, currency, isGuest);
    if (!amount) return [];

    const amountValue =
      getAmount(item.amount, currency, isGuest)?.amount ?? null;

    return [
      {
        id: `${BonusShopProductType.CASH_BONUS}-${item.id}`,
        type: BonusShopProductType.CASH_BONUS,
        order: item.order,
        group: item.group,
        img: item.img,
        label: item.label,
        title: `${amount} ${t('bonus')}`,
        typeName: t('bonus'),
        amountValue,
        subtitle: t('wager', { value: item.wager }),
        amount,
        durationTo: item.durationTo,
        cpPrice: item.cpPrice,
        productTerms: item.productTerms,
        details: toDetails([{ label: t('details.wager'), value: item.wager }]),
        versions: [],
      },
    ];
  });

  const lootboxes = (shop?.lootboxes || []).flatMap((item) => {
    const amount = formatAmount(item.winUpTo, currency, isGuest);
    if (!amount) return [];

    return [
      {
        id: `${BonusShopProductType.LOOTBOX}-${item.id}`,
        type: BonusShopProductType.LOOTBOX,
        order: item.order,
        group: item.group,
        img: item.img,
        label: item.label,
        title: item.title,
        subtitle: t('winUpTo', { amount }),
        amountValue: null,
        durationTo: item.durationTo,
        cpPrice: item.cpPrice,
        productTerms: item.productTerms,
        details: toDetails([{ label: t('details.winUpTo'), value: amount }]),
        amount: null,
        versions: [],
      },
    ];
  });

  const premium = (shop?.premium || []).flatMap((item) => {
    const amount = formatAmount(item.amount, currency, isGuest);
    if (!amount) return [];

    const amountValue =
      getAmount(item.amount, currency, isGuest)?.amount ?? null;

    return [
      {
        id: `${BonusShopProductType.PREMIUM}-${item.id}`,
        type: BonusShopProductType.PREMIUM,
        order: item.order,
        group: item.group,
        img: item.img,
        label: item.label,
        title: item.title,
        subtitle: t('premiumEquivalent', { amount }),
        amountValue,
        durationTo: item.durationTo,
        cpPrice: item.cpPrice,
        productTerms: item.productTerms,
        details: [],
        amount,
        versions: [],
      },
    ];
  });

  return [
    ...normalizeFreeSpins(freeSpins, walletCurrency, t),
    ...cash,
    ...cashBonuses,
    ...lootboxes,
    ...premium,
  ]
    .filter(({ durationTo }) => !isExpired(durationTo))
    .sort(byOrder);
};
